import { useCallback } from 'react';
import { useUserCacheStore } from '@/shared/stores/userCacheStore';

interface UseUserResolutionResult {
  resolveUserName: (userId: string | undefined) => string;
  resolveMultipleUserNames: (userIds: (string | undefined)[]) => string[];
  loading: boolean;
  error: string | null;
}

/**
 * Hook for resolving user IDs to display names.
 * Reads from the shared userCacheStore (subscribed at app level).
 */
export const useUserResolution = (): UseUserResolutionResult => {
  const userCache = useUserCacheStore((s) => s.userCache);
  const loading = useUserCacheStore((s) => s.loading);
  const error = useUserCacheStore((s) => s.error);

  const resolveUserName = useCallback(
    (userId: string | undefined): string => {
      if (!userId) return 'Unknown User';

      if (userId === 'Current User' || userId === 'current_user') {
        return 'Current User';
      }

      // Check cache
      if (userCache[userId]) {
        return userCache[userId];
      }

      // Still loading
      if (loading) {
        return 'Loading...';
      }

      // If it looks like an email, mark as unknown
      if (userId.includes('@')) {
        return 'Unknown User';
      }

      // Short non-numeric string might already be a display name
      if (userId.length <= 20 && !userId.match(/^[0-9]+$/)) {
        return userId;
      }

      return 'Unknown User';
    },
    [userCache, loading],
  );

  const resolveMultipleUserNames = useCallback(
    (userIds: (string | undefined)[]): string[] => {
      return userIds.map(resolveUserName);
    },
    [resolveUserName],
  );

  return {
    resolveUserName,
    resolveMultipleUserNames,
    loading,
    error,
  };
};
